import React, { useState, useEffect } from 'react';
import { useToast } from '../App';
import api from '../utils/api';
import LoadingState from '../components/LoadingState';

const Tags = () => {
    const { showSuccess, showError } = useToast();
    const [tags, setTags] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingTag, setEditingTag] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        slug: '',
        description: '',
    });

    useEffect(() => {
        loadTags();
    }, []);

    const loadTags = async () => {
        try {
            setLoading(true);
            const response = await api.getTags();
            if (response.success) {
                setTags(response.tags || []);
            }
        } catch (error) {
            showError('Failed to load tags');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingTag) {
                const response = await api.updateTag(editingTag.id, formData);
                if (response.success) {
                    showSuccess('Tag updated successfully');
                    loadTags();
                    resetForm();
                }
            } else {
                const response = await api.createTag(formData);
                if (response.success) {
                    showSuccess('Tag created successfully');
                    loadTags();
                    resetForm();
                }
            }
        } catch (error) {
            showError(error.message || 'Failed to save tag');
        }
    };

    const handleEdit = (tag) => {
        setEditingTag(tag);
        setFormData({
            name: tag.name,
            slug: tag.slug,
            description: tag.description || '',
        });
        setShowForm(true);
    };

    const handleDelete = async (tagId) => {
        if (!confirm('Are you sure you want to delete this tag?')) return;

        try {
            const response = await api.deleteTag(tagId);
            if (response.success) {
                showSuccess('Tag deleted successfully');
                loadTags();
            }
        } catch (error) {
            showError('Failed to delete tag');
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            slug: '',
            description: '',
        });
        setEditingTag(null);
        setShowForm(false);
    };

    return (
        <div className="page tags-page">
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <h1>Tags</h1>
                        <p>Manage product tags</p>
                    </div>
                    <div className="page-actions">
                        <button 
                            className="btn btn-primary"
                            onClick={() => setShowForm(!showForm)}
                        >
                            {showForm ? 'Cancel' : '+ Add Tag'}
                        </button>
                    </div>
                </div>

                {showForm && (
                    <div className="taxonomy-form-section">
                        <h3>{editingTag ? 'Edit Tag' : 'Add New Tag'}</h3>
                        <form onSubmit={handleSubmit}>
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Name *</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                                        required
                                        placeholder="Enter tag name"
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Slug</label>
                                    <input
                                        type="text"
                                        value={formData.slug}
                                        onChange={(e) => setFormData({...formData, slug: e.target.value})}
                                        placeholder="Auto-generated from name"
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label>Description</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    rows="3"
                                    placeholder="Optional tag description"
                                />
                            </div>
                            <div className="form-actions">
                                <button type="submit" className="btn btn-primary">
                                    {editingTag ? 'Update' : 'Create'} Tag
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                <div className="taxonomy-list">
                    {loading ? (
                        <div style={{ padding: '40px', textAlign: 'center' }}>
                            <LoadingState message="Loading tags..." />
                        </div>
                    ) : tags.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-icon">🔖</div>
                            <h3>No tags yet</h3>
                            <p>Create your first tag to get started</p>
                        </div>
                    ) : (
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Slug</th>
                                    <th>Description</th>
                                    <th>Count</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tags.map(tag => (
                                    <tr key={tag.id}>
                                        <td>
                                            <strong>{tag.name}</strong>
                                        </td>
                                        <td>{tag.slug}</td>
                                        <td>{tag.description || '-'}</td>
                                        <td>{tag.count}</td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleEdit(tag)}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleDelete(tag.id)}
                                                style={{marginLeft: '8px'}}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Tags;
